import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Loader2, Plus, Eye, Trash2, Building2, Mail, Phone, Calendar, Handshake, MapPin, Globe, Link as LinkIcon, Briefcase, DollarSign, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../../context/WorkspaceContext';
import { crmAPI, formatIDR } from '../../lib/crm';

export default function ClientsPage() {
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [leads, setLeads] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);

    const [form, setForm] = useState(initialClientForm());

    function initialClientForm() {
        return {
            partnership_id: '',
            sales_id: '',
            account_manager_ids: [],
            first_project: {
                name: '', value: 0, description: '',
                year: new Date().getFullYear(),
                terms_of_payment: 1,
                start_date: new Date().toISOString().slice(0, 10),
                due_date: '',
            },
        };
    }

    const load = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const [cres, lres, mres] = await Promise.all([
                crmAPI.listClients(currentWorkspace.id),
                crmAPI.listLeads(currentWorkspace.id),
                crmAPI.listMembers(currentWorkspace.id),
            ]);
            setRows(cres.data || []);
            setLeads(lres.data || []);
            setMembers(mres.data || []);
        } catch (e) {
            toast.error('Gagal memuat Clients');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => { load(); }, [load]);

    // Stats
    const totalProjects = rows.reduce((s, c) => s + (c.total_projects || 0), 0);
    const totalValue = rows.reduce((s, c) => s + (c.total_value || 0), 0);

    // Available leads = not yet a client
    const clientPartnershipIds = new Set(rows.map((r) => r.partnership_id));
    const availableLeads = leads.filter((l) => !clientPartnershipIds.has(l.partnership_id));

    const openCreate = () => { setForm(initialClientForm()); setFormOpen(true); };

    const save = async () => {
        const { partnership_id, sales_id, first_project } = form;
        if (!partnership_id) return toast.error('Pilih Lead / Data Company');
        if (!sales_id) return toast.error('Pilih Sales');
        if (!first_project.name?.trim()) return toast.error('Nama project wajib');
        if (!first_project.due_date) return toast.error('Due date project wajib');
        try {
            setSaving(true);
            await crmAPI.createClient({
                workspace_id: currentWorkspace.id,
                ...form,
                first_project: {
                    ...first_project,
                    value: Number(first_project.value) || 0,
                    year: parseInt(first_project.year, 10),
                    terms_of_payment: parseInt(first_project.terms_of_payment, 10) || 1,
                },
            });
            toast.success('Client + First Project dibuat');
            setFormOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Hapus client "${row.partnership?.company_name}"? Semua project akan ikut terhapus.`)) return;
        try {
            await crmAPI.deleteClient(row.id);
            toast.success('Dihapus');
            await load();
        } catch (e) { toast.error('Gagal'); }
    };

    const toggleAM = (userId) => {
        setForm((f) => ({
            ...f,
            account_manager_ids: f.account_manager_ids.includes(userId)
                ? f.account_manager_ids.filter((u) => u !== userId)
                : [...f.account_manager_ids, userId],
        }));
    };

    return (
        <Layout title="Client" subtitle="Perusahaan yang telah menandatangani kontrak">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                <StatCard label="Total Project" value={totalProjects} icon={Briefcase} testid="stat-total-projects" />
                <StatCard label="Total Nilai Project" value={formatIDR(totalValue)} icon={DollarSign} testid="stat-total-value" />
            </div>

            <div className="flex justify-end mb-4">
                <Button onClick={openCreate} data-testid="client-create-btn">
                    <Plus className="w-4 h-4 mr-2" /> Tambah Client (dari Lead)
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
            ) : rows.length === 0 ? (
                <div className="text-center py-20 text-brand-text-muted">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Belum ada Client</p>
                </div>
            ) : (
                <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Perusahaan</TableHead>
                                <TableHead>PIC</TableHead>
                                <TableHead>Position</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Total Project</TableHead>
                                <TableHead>Total Nilai</TableHead>
                                <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`client-row-${r.id}`}>
                                    <TableCell className="font-medium">{r.partnership?.company_name || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_name || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_title || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_email || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_phone || '-'}</TableCell>
                                    <TableCell>{r.total_projects}</TableCell>
                                    <TableCell>{formatIDR(r.total_value)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="icon" onClick={async () => {
                                                const res = await crmAPI.getClient(r.id);
                                                setDetail(res.data);
                                            }} data-testid={`client-detail-${r.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`client-delete-${r.id}`}>
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form">
                    <DialogHeader><DialogTitle>Tambah Client dari Lead</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Pilih Data Company dari Lead *</Label>
                            <Select value={form.partnership_id} onValueChange={(v) => setForm({ ...form, partnership_id: v })}>
                                <SelectTrigger data-testid="client-lead-select"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                                <SelectContent>
                                    {availableLeads.length === 0 ? (
                                        <div className="px-2 py-3 text-sm text-brand-text-muted">Tidak ada Lead yang belum jadi Client</div>
                                    ) : availableLeads.map((l) => (
                                        <SelectItem key={l.id} value={l.partnership_id}>{l.partnership?.company_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Sales *</Label>
                                <Select value={form.sales_id} onValueChange={(v) => setForm({ ...form, sales_id: v })}>
                                    <SelectTrigger data-testid="client-sales-select"><SelectValue placeholder="Pilih sales..." /></SelectTrigger>
                                    <SelectContent>
                                        {members.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Account Manager(s)</Label>
                                <div className="border border-brand-border rounded-lg p-2 max-h-32 overflow-y-auto" data-testid="client-am-list">
                                    {members.length === 0 ? <div className="text-xs text-brand-text-muted p-1">Tidak ada member</div> :
                                        members.map((m) => (
                                            <label key={m.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                                                <input type="checkbox" checked={form.account_manager_ids.includes(m.id)} onChange={() => toggleAM(m.id)} data-testid={`am-${m.id}`} />
                                                {m.name}
                                            </label>
                                        ))}
                                </div>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <div className="font-semibold mb-2">First Project</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Nama Project *" value={form.first_project.name} onChange={(v) => setForm({ ...form, first_project: { ...form.first_project, name: v } })} testid="fp-name" />
                                <CurrencyField label="Nilai Project (IDR)" value={form.first_project.value} onChange={(v) => setForm({ ...form, first_project: { ...form.first_project, value: v } })} testid="fp-value" />
                                <Field label="Tahun *" type="number" value={form.first_project.year} onChange={(v) => setForm({ ...form, first_project: { ...form.first_project, year: v } })} testid="fp-year" />
                                <Field label="Jumlah Termin of Payment *" type="number" min={1} value={form.first_project.terms_of_payment} onChange={(v) => setForm({ ...form, first_project: { ...form.first_project, terms_of_payment: v } })} testid="fp-terms" />
                                <Field label="Start Date *" type="date" value={form.first_project.start_date} onChange={(v) => setForm({ ...form, first_project: { ...form.first_project, start_date: v } })} testid="fp-start" />
                                <Field label="Due Date *" type="date" value={form.first_project.due_date} onChange={(v) => setForm({ ...form, first_project: { ...form.first_project, due_date: v } })} testid="fp-due" />
                            </div>
                            <div className="mt-3">
                                <Label>Deskripsi</Label>
                                <Textarea rows={2} value={form.first_project.description} onChange={(e) => setForm({ ...form, first_project: { ...form.first_project, description: e.target.value } })} data-testid="fp-desc" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
                        <Button onClick={save} disabled={saving} data-testid="client-save-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-detail">
                    {detail && <ClientDetail client={detail} />}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function StatCard({ label, value, icon: Icon, testid }) {
    return (
        <div className="bg-white border border-brand-border rounded-lg p-4 flex items-center gap-3" data-testid={testid}>
            <div className="w-10 h-10 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                <Icon className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
                <div className="text-xs text-brand-text-muted">{label}</div>
                <div className="text-lg font-semibold">{value}</div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', testid, min }) {
    return (
        <div>
            <Label>{label}</Label>
            <Input type={type} min={min} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}

function CurrencyField({ label, value, onChange, testid }) {
    const formatDisplay = (num) => {
        if (num == null || num === '') return '';
        return 'IDR ' + Number(num).toLocaleString('id-ID');
    };

    const handleChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        const num = raw === '' ? 0 : parseInt(raw, 10);
        onChange(num);
    };

    return (
        <div>
            <Label>{label}</Label>
            <Input
                type="text"
                value={formatDisplay(value)}
                onChange={handleChange}
                placeholder="IDR 0"
                data-testid={testid}
            />
        </div>
    );
}

function ClientDetail({ client }) {
    const p = client.partnership || {};
    return (
        <div>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-brand-primary" /> {p.company_name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <InfoLine icon={Handshake} label="PIC" value={`${p.pic_name || '-'} — ${p.pic_title || ''}`} />
                    <InfoLine icon={Mail} label="Email" value={p.pic_email} />
                    <InfoLine icon={Phone} label="Phone" value={p.pic_phone} />
                    <InfoLine icon={LinkIcon} label="LinkedIn" value={p.pic_linkedin} href={p.pic_linkedin} />
                    <InfoLine icon={Globe} label="Website" value={p.company_website} href={p.company_website} />
                    <InfoLine icon={MapPin} label="Alamat" value={p.company_address} />
                    <InfoLine icon={Calendar} label="Last Contact" value={p.last_contact} />
                    <InfoLine icon={Calendar} label="Next Contact" value={p.next_contact} />
                    <InfoLine icon={Briefcase} label="Total Project" value={client.total_projects} />
                    <InfoLine icon={DollarSign} label="Total Nilai" value={formatIDR(client.total_value)} />
                    <InfoLine icon={Users} label="Sales" value={client.sales?.name} />
                    <InfoLine icon={Users} label="Account Managers" value={(client.account_managers || []).map((a) => a.name).join(', ') || '-'} />
                </div>

                <div className="border-t pt-4">
                    <div className="text-sm font-semibold mb-2">List Project</div>
                    {(client.projects || []).length === 0 ? <div className="text-sm text-brand-text-muted">Belum ada project</div> : (
                        <div className="space-y-2">
                            {client.projects.map((pr) => (
                                <div key={pr.id} className="p-3 bg-brand-accent-soft/30 rounded-lg">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-medium">{pr.name}</div>
                                            <div className="text-xs text-brand-text-muted">{pr.start_date} → {pr.due_date}</div>
                                        </div>
                                        <div className="text-sm font-semibold text-brand-primary">{formatIDR(pr.value)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InfoLine({ icon: Icon, label, value, href }) {
    return (
        <div className="flex items-start gap-2">
            <Icon className="w-4 h-4 mt-0.5 text-brand-text-muted flex-shrink-0" />
            <div>
                <div className="text-xs text-brand-text-muted">{label}</div>
                {href && value ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline break-all">{value}</a> : <div className="break-words">{value != null && value !== '' ? value : '-'}</div>}
            </div>
        </div>
    );
}
